<script setup lang="ts">
import {computed, ref, watch} from "vue";
import {useNow} from "@vueuse/core";
import {ArrowRight, TriangleAlert} from "@lucide/vue";
import {
    AlertDialog,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle
} from "@/core/components/ui/alert-dialog";
import {Button} from "@/core/components/ui/button";
import {Input} from "@/core/components/ui/input";
import {Label} from "@/core/components/ui/label";
import {AgendaAppointment} from "@/core/interfaces/AgendaAppointment";
import {HealthcenterOpening} from "@/core/interfaces/healthcenter/Healthcenter";
import {earliestStart, formatAgendaTime, isPastStart, parseAgendaDate} from "@/agenda/lib/agendaTime";
import {isClosedAt} from "@/agenda/lib/openingHours";
import {PAST_APPOINTMENT_MESSAGE, START_STEP_MINUTES} from "@/agenda/constants";
import {appointmentLabel, appointmentPatient} from "@/agenda/lib/appointmentLabel";
import {emptyPatientSmsChoice, isPatientSmsChoiceValid, PatientSmsChoice} from "@/agenda/lib/patientSms";
import AgendaPatientSmsOption from "@/agenda/components/AgendaPatientSmsOption.vue";

const props = defineProps<{
    /** The appointment to move, null when no move is pending. */
    appointment: AgendaAppointment | null;
    /**
     * Where a drag dropped it. Without it the dialog asks for the new date
     * and time, as when « Déplacer » is chosen from the detail.
     */
    start?: Date | null;
    /** Names a typed start that falls while the pharmacy is closed. */
    opening?: HealthcenterOpening | null;
    loading?: boolean;
}>();

const emits = defineEmits<{
    (e: 'confirm', sms: PatientSmsChoice, start: Date): void;
}>();

const open = defineModel<boolean>('open', {required: true});

const sms = ref<PatientSmsChoice>(emptyPatientSmsChoice());

const pickedDate = ref('');

const pickedTime = ref('');

const picking = computed(() => !props.start);

watch(open, (isOpen) => {
    if (!isOpen) {
        return;
    }

    sms.value = emptyPatientSmsChoice();

    if (props.appointment) {
        const current = parseAgendaDate(props.appointment.start_at);

        pickedDate.value = current.format('YYYY-MM-DD');
        pickedTime.value = current.format('HH:mm');
    }
});

const now = useNow({interval: 1000});

const chosenStart = computed<Date | null>(() => {
    if (!picking.value) {
        return props.start ?? null;
    }

    return pickedDate.value && pickedTime.value ? new Date(`${pickedDate.value}T${pickedTime.value}`) : null;
});

/** A typed start equal to the current one moves nothing. */
const moved = computed(() => {
    return !!props.appointment && !!chosenStart.value
        && !parseAgendaDate(chosenStart.value).isSame(parseAgendaDate(props.appointment.start_at), 'minute');
});

const startsInPast = computed(() => !!chosenStart.value && isPastStart(chosenStart.value, now.value));

/**
 * Said, not enforced, as in the form: the pharmacist may be booking an
 * opening on appointment. A drop needs no warning, the grid greys the slot.
 */
const startsWhileClosed = computed(() => {
    return picking.value && moved.value && !!chosenStart.value
        && isClosedAt(props.opening, parseAgendaDate(chosenStart.value));
});

const minDate = computed(() => parseAgendaDate(now.value).format('YYYY-MM-DD'));

/** Aligned on the input's step, which the browser counts from `min`. */
const minTime = computed(() => {
    return pickedDate.value === minDate.value ? earliestStart(now.value).format('HH:mm') : undefined;
});

/** The format the move is sent in, so the preview names the same time. */
const startAt = computed(() => chosenStart.value ? parseAgendaDate(chosenStart.value).format() : null);

const label = computed(() => {
    return props.appointment ? appointmentLabel(props.appointment) : 'Ce rendez-vous';
});

const patientName = computed(() => appointmentPatient(props.appointment));

/** A vertical drag stays on the same day, so the date is stated once. */
const sameDay = computed(() => {
    return !!props.appointment && !!chosenStart.value
        && parseAgendaDate(props.appointment.start_at).isSame(parseAgendaDate(chosenStart.value), 'day');
});

const dayLabel = computed(() => {
    return chosenStart.value ? parseAgendaDate(chosenStart.value).format('dddd D MMMM') : '';
});

const previousSchedule = computed(() => {
    if (!props.appointment) {
        return '';
    }

    const time = formatAgendaTime(props.appointment.start_at);

    return sameDay.value
        ? time
        : `${parseAgendaDate(props.appointment.start_at).format('dddd D MMMM')} à ${time}`;
});

const nextSchedule = computed(() => {
    if (!chosenStart.value) {
        return '';
    }

    const time = formatAgendaTime(chosenStart.value);

    return sameDay.value ? time : `${dayLabel.value} à ${time}`;
});

const canConfirm = computed(() => {
    return moved.value && !startsInPast.value && isPatientSmsChoiceValid(sms.value) && !props.loading;
});

const confirm = () => {
    if (chosenStart.value) {
        emits('confirm', sms.value, chosenStart.value);
    }
};
</script>

<template>
  <AlertDialog v-model:open="open">
    <AlertDialogContent class="z-[60]">
      <AlertDialogHeader>
        <AlertDialogTitle>Déplacer ce rendez-vous ?</AlertDialogTitle>
        <AlertDialogDescription>
          {{ label }}{{ patientName ? ` — ${patientName}` : '' }}
        </AlertDialogDescription>
      </AlertDialogHeader>
      <div class="flex flex-col gap-3 text-sm">
        <div v-if="picking" class="grid grid-cols-[minmax(0,1fr)_120px] items-end gap-3">
          <div class="flex flex-col gap-1.5">
            <Label for="agenda-reschedule-date">Date</Label>
            <Input id="agenda-reschedule-date" v-model="pickedDate" type="date" :min="minDate" />
          </div>
          <div class="flex flex-col gap-1.5">
            <Label for="agenda-reschedule-time">Heure</Label>
            <Input
              id="agenda-reschedule-time"
              v-model="pickedTime"
              type="time"
              :min="minTime"
              :step="START_STEP_MINUTES * 60"
            />
          </div>
        </div>
        <p v-if="sameDay && !picking" class="font-medium first-letter:uppercase">
          {{ dayLabel }}
        </p>
        <div v-if="moved" class="flex flex-wrap items-center gap-2 tabular-nums">
          <span class="text-gray-500 line-through first-letter:uppercase">{{ previousSchedule }}</span>
          <ArrowRight class="size-4 shrink-0 text-gray-400" />
          <span class="font-medium first-letter:uppercase">{{ nextSchedule }}</span>
        </div>
        <!-- A drop is refused in the past before the dialog opens, but its slot
             can pass while the dialog stays open. -->
        <p v-if="moved && startsInPast" class="text-xs text-red-600">
          {{ PAST_APPOINTMENT_MESSAGE }}
        </p>
        <p v-else-if="startsWhileClosed" class="flex items-center gap-1.5 text-xs text-gray-dark">
          <TriangleAlert class="size-3.5 shrink-0 text-orange" />
          La pharmacie est fermée sur ce créneau. Vous pouvez tout de même déplacer le rendez-vous.
        </p>
        <AgendaPatientSmsOption
          v-if="appointment?.customer && moved"
          v-model="sms"
          :appointment-id="appointment.id"
          event="rescheduled"
          :start-at="startAt"
        />
      </div>
      <AlertDialogFooter>
        <AlertDialogCancel>Annuler</AlertDialogCancel>
        <!-- Not an AlertDialogAction, which closes on click: the dialog stays
             open until the move succeeds, so a refusal keeps what was typed. -->
        <Button class="bg-primary hover:bg-primary/90" :disabled="!canConfirm" @click="confirm">
          Déplacer le rendez-vous
        </Button>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
</template>
